import {HospitalProfile} from '@/interface';
import {Button, IconType, Icon, StatusBadge, Text} from '@/ui-kits';
import React, { useMemo } from 'react';
import {ProfileDetailSections} from './ProfileDetailSections';
import {HospitalTypeLabel, StatusBadgeVariant} from '@/constants';
import {cn, formatName} from '@/utils/common_functions';
import {palette} from '@/theme';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface HospitalProfileViewProps {
  profileData: HospitalProfile;
  handleEdit?: () => void;
  handleBookNow?: () => void;
  viewOnly?: boolean;
}

interface HospitalInfo {
  icon: IconType;
  label: string;
  value: string;
}

export const HospitalProfileView: React.FC<HospitalProfileViewProps> = (props) => {
  const {profileData, handleEdit, handleBookNow, viewOnly = false} = props;
  const navigate = useNavigate();
  const hospital_info: HospitalInfo[] = [
    {
      icon: 'phone_blue',
      label: 'Phone',
      value: profileData.phone,
    },
    {
      icon: 'email_blue',
      label: 'Email',
      value: profileData.email,
    },
    {
      icon: 'location_blue',
      label: 'Address',
      value: `${formatName(profileData.address)}, ${formatName(profileData.city)}, ${formatName(profileData.state)}, ${formatName(profileData.country?.label ?? "")}`,
    },
    {
      icon: 'cash_blue2',
      label: 'Consultation Fee',
      value: `${profileData.consultation_fee?.currency?.symbol ?? '₹'}${profileData.consultation_fee?.amount ?? ''}`,
    },
  ];

  const HospitalInfoComponent = ({className}: {className?: string}) => (
    <div className={cn('grid md:grid-cols-2 gap-4 w-full', className)}>
      {hospital_info.map(info => (
        <div key={info.label} className="flex gap-4 min-w-0 w-full overflow-hidden">
          <Icon type={info.icon} className="flex-shrink-0 mt-2" />
          <div className="flex flex-col gap-1 min-w-0 flex-1 w-full overflow-hidden">
            <Text variant="body" className="text-ui_input_border_dark !font-FigtreeMeduim">
              {info.label}
            </Text>
            <Text variant="body" className="text-ui_blue break-words break-all !font-headingSemiBold min-w-0 w-full">
              {info.value}
            </Text>
          </div>
        </div>
      ))}
    </div>
  );

  const specializations = useMemo(() => {
    if (viewOnly) {
      return profileData.specializations.filter(item => item.status)
    }
    return profileData.specializations;
  }, [profileData.specializations, viewOnly]);

  return (
    <>
      <div className="bg-gray-50 p-3 sm:p-4 md:p-6 border border-ui_input_border rounded-xl flex flex-col gap-4">
        {/* section header */}
        <div className="flex sm:flex-row flex-col gap-3 justify-between sm:items-center">
          <Text variant="subheading" className="text-ui_blue inline-flex items-center gap-1">
            Hospital Details
          </Text>

          <div className="flex items-center gap-6 flex-wrap justify-start">
            {viewOnly && (
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <Icon type="orange_clock" className="w-4 h-4 flex-shrink-0" />
                <Text
                  variant="body"
                  className="text-ui_input_border_dark !font-FigtreeMeduim text-xs sm:text-sm"
                >
                  Response within 48 hours
                </Text>
              </div>
            )}

            <Button
              title={viewOnly ? "Book Now" : "Edit Profile"}
              className="w-auto max-w-[120px] sm:max-w-[150px] flex-shrink-0 px-3 sm:px-4"
              onClick={viewOnly ? handleBookNow : handleEdit}
            />
          </div>
        </div>

        {/* section content */}
        <div className="flex gap-5 md:flex-row flex-col">
          {/* image */}
          <img src={profileData.image} className="size-[200px] rounded-md" />

          {/* hospital details */}
          <div className="grow flex flex-col gap-3">
            {/* heading */}
            <div className="flex gap-4 items-center hospital-profile-view-heading">
              <Text className="text-ui_blue hospital-profile-view-heading-name" variant="heading">
                {profileData.name}
              </Text>
              <StatusBadge
                className="hospital-profile-view-heading-badge"
                textClassName="!font-FigtreeRegular text-nowrap"
                status={StatusBadgeVariant.BLUE}
                children={HospitalTypeLabel[profileData.hospital_type.id]}
              />
            </div>

            {/* details */}
            <HospitalInfoComponent className="hospital-profile-details-card-lg" />
          </div>
        </div>

        <HospitalInfoComponent className="hospital-profile-details-card-sm" />
      </div>

      <ProfileDetailSections wrapperClassName="flex flex-col gap-4" icon="certification" title="Hospital Overview">
        <div className="bg-white p-4 gap-2 rounded-lg">
          <Text variant="subtitle" className="text-ui_input_border_dark">
            About
          </Text>
          <Text className="text-ui_blue">{profileData.about}</Text>
        </div>
        {profileData.accreditation && (
          <div className="bg-white p-4 gap-2 rounded-lg">
            <Text variant="subtitle" className="text-ui_input_border_dark">
              Accreditation
            </Text>
            <Text className="text-ui_blue !font-FigtreeSemiBold">{profileData.accreditation}</Text>
          </div>
        )}
      </ProfileDetailSections>

      <ProfileDetailSections wrapperClassName="flex flex-wrap items-center gap-4" icon="stethoscope2" title="Specialization">
        
        {specializations.map(item => (
          <div
            key={item.id}
            style={{
              backgroundColor: item.status && !viewOnly ? `${palette.ui_bg_light_blue}` : 'white',
            }}
            className={cn('border border-ui_input_border_dark rounded-lg max-w-full px-2 sm:px-4 py-1 sm:py-2')}>
            <Text className="text-ui_blue break-all !font-FigtreeSemiBold max-w-full">{item.label}</Text>
          </div>
        ))}
      </ProfileDetailSections>
    </>
  );
};
